import jsPDF from "jspdf";
import defaultProfileImage from '../assets/image/demo.jpeg';

// Hall ticket PDF for one candidate, opened in a new tab.
// Shared by EditTest (chapter tests) and BatchTestDetail (institute tests).
export const downloadHallTicket = async (test, candidate = {}) => {
  const doc = new jsPDF();

  const primaryColor   = [30, 11, 104];
  const secondaryColor = [52, 73, 94];
  const lightGray      = [236, 240, 241];

  // ── FIX: Load logo as base64 via fetch ───────────────────────────────────
  const getBase64FromUrl = async (url) => {
    try {
      const res  = await fetch(url);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result); // returns "data:image/png;base64,..."
        reader.onerror   = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null; // if logo fails to load, skip it gracefully
    }
  };

  // jsPDF's addImage needs to know the real codec to use (PNG vs JPEG decoder).
  // The mime label on a data URL can't be trusted here — the backend always
  // reports candidate photos as image/jpeg even when the uploaded file is a
  // PNG/GIF/WEBP — so sniff the actual format from the base64 magic bytes.
  const getImageFormatFromBase64 = (dataUrl) => {
    const base64 = (dataUrl || '').split(',')[1] || '';
    if (base64.startsWith('iVBORw0KGgo')) return 'PNG';
    if (base64.startsWith('/9j/'))        return 'JPEG';
    if (base64.startsWith('R0lGOD'))      return 'GIF';
    if (base64.startsWith('UklGR'))       return 'WEBP';
    return 'JPEG'; // sensible fallback; most uploaded photos are JPEGs
  };

  const logoBase64 = await getBase64FromUrl('/assets/img/mensatrans.png');

  // ── Header ───────────────────────────────────────────────────────────────
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 35, 'F');

  // Only add logo if it loaded successfully
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, getImageFormatFromBase64(logoBase64), 5, 5, 70, 20);
    } catch (err) {
      console.error('Failed to embed logo in hall ticket:', err);
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('HALL TICKET', 105, 15, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('EXAMINATION ADMIT CARD', 105, 25, { align: 'center' });

  const hallTicketNo = candidate?.hall_ticket_no || '';
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('HallTicket No:', 200, 18, { align: 'right' });
  doc.text(hallTicketNo,     200, 25, { align: 'right' });

  doc.setTextColor(0, 0, 0);

  // ── Candidate fields ─────────────────────────────────────────────────────
  const Candidatefname  = candidate.first_name    || '';
  const Candidatelname  = candidate.last_name     || '';
  const CandidateEmail  = candidate.email         || 'Not Provided';
  const CandidateMobile = candidate.mobile_no     || 'Not Provided';
  const Candidategender = candidate.gender        || 'Not Specified';
  const Candidateaadhar = candidate.aadhar_last || '-';
  const Candidatedob = candidate.dob || '-';
  const profilePic      = candidate.profile_image || null;
  const maskAadhar = (aadhar) => {
    if (!aadhar) return "Not Specified";

    const clean = aadhar.toString().replace(/\s+/g, "");

    // Treat 0000 as not provided
    if (clean === "0000") return "Not Specified";

    if (clean.length !== 4) return "Invalid Aadhaar";

    return "xxxx xxxx " + clean;
  };

  // ── Profile photo (also needs base64 if it's a URL) ──────────────────────
  let profileBase64 = null;
  if (profilePic) {
    profileBase64 = await getBase64FromUrl(profilePic);
  }
  // No candidate photo on file (or it failed to load) — fall back to the
  // default placeholder photo instead of an empty gray box.
  if (!profileBase64) {
    profileBase64 = await getBase64FromUrl(defaultProfileImage);
  }

  let profileImageDrawn = false;
  if (profileBase64) {
    try {
      doc.addImage(profileBase64, getImageFormatFromBase64(profileBase64), 150, 45, 35, 45);
      profileImageDrawn = true;
    } catch (err) {
      // A stored photo that's genuinely corrupt/unsupported shouldn't stop
      // the rest of the hall ticket from generating — fall back to the
      // placeholder box below instead of throwing.
      console.error('Failed to embed candidate photo in hall ticket:', err);
    }
  }
  if (!profileImageDrawn) {
    doc.setFillColor(...lightGray);
    doc.rect(150, 45, 35, 45, 'F');
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('CANDIDATE', 167.5, 65, { align: 'center' });
    doc.text('PHOTO',     167.5, 72, { align: 'center' });
  }

  // ── Candidate Details ─────────────────────────────────────────────────────
  doc.setTextColor(...secondaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CANDIDATE DETAILS', 20, 55);

  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(20, 58, 140, 58);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const candidateInfo = [
    { label: 'Name:',              value: `${Candidatefname} ${Candidatelname}`.trim() || 'CANDIDATE NAME' },
    { label: 'Email:',             value: CandidateEmail },
    { label: 'Mobile:',            value: CandidateMobile },
    { label: 'Gender:',            value: Candidategender },
    { 
      label: 'DOB:',
      value: Candidatedob
        ? new Date(Candidatedob).toLocaleDateString('en-GB')
        : 'Not Specified'
    },
    { label: 'Registration Date:', value: new Date(test.register_start_date).toLocaleString('en-GB') },
    { label: 'Aadhar Number:', value: maskAadhar(Candidateaadhar) },
  ];

  let yPos = 68;
  candidateInfo.forEach(info => {
    doc.setFont('helvetica', 'bold');
    doc.text(info.label, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(info.value, 60, yPos);
    yPos += 8;
  });

  // ── Exam Details ──────────────────────────────────────────────────────────
  doc.setTextColor(...secondaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('EXAMINATION DETAILS', 20, 125);
  doc.line(20, 128, 190, 128);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const testInfo = [
    { label: 'Test Title:',        value: test.test_title || 'TEST NAME' },
    { label: 'Test Desc:',         value: test.test_desc  || '' },
    { label: 'Start Date & Time:', value: new Date(test.test_start_time).toLocaleString('en-GB') },
    { label: 'End Date & Time:',   value: new Date(test.test_end_time).toLocaleString('en-GB') },
    { label: 'Venue:',             value: test.venue || 'EXAMINATION CENTER' },
  ];

  yPos = 138;
  testInfo.forEach(info => {
      doc.setFont('helvetica', 'bold');
      doc.text(info.label, 20, yPos);
      doc.setFont('helvetica', 'normal');

      // Split long text to fit within page width (max width = 120 units from x=70)
      const maxWidth = 120;
      const lines = doc.splitTextToSize(info.value, maxWidth);
      doc.text(lines, 70, yPos);

      // Move yPos down based on number of lines (8 per line)
      yPos += lines.length > 1 ? lines.length * 7 : 8;
  });

  // ── Instructions ──────────────────────────────────────────────────────────
  doc.setTextColor(...secondaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('IMPORTANT INSTRUCTIONS', 20, 210);
  doc.line(20, 213, 190, 213);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const instructions = [
    '1. Candidates must bring this hall ticket to the examination center.',
    '2. Carry a valid photo ID proof along with the hall ticket.',
    '3. Report to the examination center 30 minutes before the scheduled time.',
    '4. Mobile phones and electronic devices are strictly prohibited.',
    '5. Late entry will not be allowed under any circumstances.',
  ];

  yPos = 223;
  instructions.forEach(i => { doc.text(i, 20, yPos); yPos += 6; });

  // ── Footer ────────────────────────────────────────────────────────────────
  doc.setFillColor(...lightGray);
  doc.rect(0, 270, 210, 27, 'F');

  doc.setTextColor(...secondaryColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('This is a computer-generated hall ticket. No signature is required.', 105, 280, { align: 'center' });
  doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 105, 287, { align: 'center' });

  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1);
  doc.rect(5, 5, 200, 287);

  // ── Open as PDF in new tab ────────────────────────────────────────────────
  const pdfBlobUrl = doc.output('bloburl');
  window.open(pdfBlobUrl, '_blank');
};
